import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { $createPageSorted } from "../page/create-page.atom";

const logger = new Logger("today-page");

const ensurePageLocks = new Map<string, boolean>();

export interface DatePageResult {
	dayPage: PageMetadata;
	slugPath: string;
}

interface DateInfo {
	year: string;
	month: string;
	day: string;
	dayTitle: string;
	/** The page ID and slug in YYYY-MM-DD format */
	dateSlug: string;
}

function getDateInfo(date: Date = new Date()): DateInfo {
	const year = date.getFullYear().toString();
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const day = date.getDate().toString().padStart(2, "0");
	const dayTitle = date.toLocaleDateString(undefined, {
		weekday: "short",
		year: "numeric",
		month: "long",
		day: "numeric",
	});
	const dateSlug = `${year}-${month}-${day}`;
	return { year, month, day, dayTitle, dateSlug };
}

/**
 * Parses a slug like "2026-01-22" into date info.
 * Returns undefined if the slug is not a valid date.
 */
export function parseDateSlug(slug: string): DateInfo | undefined {
	// Match YYYY-MM-DD format
	const match = slug.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!match) {
		return undefined;
	}

	const [, yearStr, monthStr, dayStr] = match;

	// Validate year (4 digits, reasonable range)
	const yearNum = parseInt(yearStr, 10);
	if (yearNum < 1900 || yearNum > 9999) {
		return undefined;
	}

	// Validate month (01-12)
	const monthNum = parseInt(monthStr, 10);
	if (monthNum < 1 || monthNum > 12) {
		return undefined;
	}

	// Validate day (01-31)
	const dayNum = parseInt(dayStr, 10);
	if (dayNum < 1 || dayNum > 31) {
		return undefined;
	}

	// Create a date object to get proper titles
	const date = new Date(yearNum, monthNum - 1, dayNum);

	// Verify the date is valid (e.g., not Feb 31)
	if (
		date.getFullYear() !== yearNum ||
		date.getMonth() !== monthNum - 1 ||
		date.getDate() !== dayNum
	) {
		return undefined;
	}

	return getDateInfo(date);
}

/**
 * Comparator for sorting daily notes pages by date, newest first.
 * Root pages (date pages) are sorted by their slug (YYYY-MM-DD format) in descending order.
 * Subpages are grouped after their parent and sorted by their original order.
 */
export function compareDailyNotesPages(a: PageMetadata, b: PageMetadata): number {
	const aIsRoot = a.parentPageID === null;
	const bIsRoot = b.parentPageID === null;

	// Both are root pages: sort by slug descending (newest first)
	if (aIsRoot && bIsRoot) {
		return b.slug.localeCompare(a.slug);
	}

	// One is root, one is subpage: root pages come before subpages at the same level
	// But since this is a flat sort, we need to determine relative ordering based on parents
	if (aIsRoot && !bIsRoot) {
		// If b is a child of a, b should come after a
		if (b.parentPageID === a.id) {
			return -1;
		}
		// Otherwise, compare a's slug with b's parent slug
		// b should be positioned relative to its parent
		return b.parentPageID!.localeCompare(a.slug) || -1;
	}

	if (!aIsRoot && bIsRoot) {
		// If a is a child of b, a should come after b
		if (a.parentPageID === b.id) {
			return 1;
		}
		// Otherwise, compare a's parent slug with b's slug
		return b.slug.localeCompare(a.parentPageID!) || 1;
	}

	// Both are subpages: if they share the same parent, maintain original order (stable sort)
	// If different parents, sort by parent slug descending
	if (a.parentPageID === b.parentPageID) {
		// Same parent: maintain original order (return 0 for stable sort)
		return 0;
	}

	// Different parents: sort by parent slug descending
	return b.parentPageID!.localeCompare(a.parentPageID!);
}

/**
 * Ensures a date page exists for the given documentId and date slug.
 * Creates a flat page in the daily/ folder if it doesn't exist.
 */
export const $ensureDatePage = atomFamily((params: { documentId: string; slugPath: string }) => {
	const { documentId, slugPath } = params;

	const $ensureDatePageAtom = atom(
		null,
		async (get, set): Promise<DatePageResult | undefined> => {
			const dateInfo = parseDateSlug(slugPath);
			if (!dateInfo) {
				logger.debug("Invalid date slug, skipping", { documentId, slugPath });
				return undefined;
			}

			const { dateSlug, dayTitle } = dateInfo;
			const lockKey = `${documentId}/${dateSlug}`;

			if (ensurePageLocks.get(lockKey)) {
				logger.debug("Already ensuring date page, skipping", { documentId, lockKey });
				return undefined;
			}

			ensurePageLocks.set(lockKey, true);

			try {
				logger.debug("Ensuring date page", { documentId, dateSlug });

				const metadataKey = metadataKeyForDocsRoute(documentId);
				const metadata = get($documentMetadata(metadataKey));

				if (!metadata) {
					throw new Error(`Document metadata not found for ${documentId}`);
				}

				const pages = metadata.pagesMetadata;

				// Check if the page already exists
				const existingPage = pages.find((p) => p.id === dateSlug);

				if (existingPage) {
					logger.debug("Date page already exists", { documentId, dateSlug });
					return {
						dayPage: existingPage,
						slugPath: dateSlug,
					};
				}

				logger.debug("Creating date page", { dateSlug });
				const dayPage = await set($createPageSorted, {
					documentId,
					pageType: "markdown",
					path: `daily/${dateSlug}.md`,
					pageMetadata: {
						id: dateSlug,
						title: dayTitle,
						slug: dateSlug,
						parentPageID: null,
					},
					comparator: compareDailyNotesPages,
				});

				logger.debug("Date page ready", { documentId, slugPath: dateSlug });

				return {
					dayPage,
					slugPath: dateSlug,
				};
			} finally {
				ensurePageLocks.delete(lockKey);
			}
		}
	);
	$ensureDatePageAtom.debugLabel = `ensureDatePage/${documentId}/${slugPath}`;
	return $ensureDatePageAtom;
}, isEqual);

/**
 * Ensures today's page exists for the given documentId.
 * This creates a write-only atom that computes today's date fresh on each call,
 * avoiding stale date issues in long-running sessions.
 */
export const $ensureTodayPage = atomFamily((documentId: string) => {
	const $atom = atom(null, async (_get, set): Promise<DatePageResult | undefined> => {
		const { dateSlug } = getDateInfo();
		return set($ensureDatePage({ documentId, slugPath: dateSlug }));
	});
	$atom.debugLabel = `ensureTodayPage/${documentId}`;
	return $atom;
});

/**
 * Returns today's slug path (e.g., "2026-01-21").
 * Computes the date fresh on each read to avoid stale values in long-running sessions.
 */
export function getTodaySlugPath(): string {
	const { dateSlug } = getDateInfo();
	return dateSlug;
}
